import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import { FiPlay } from 'react-icons/fi';

import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import { Heading } from '@/components/typography/heading';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import type { RequestLog, ResponseLog } from '@/types/logs';
import { buildLogsFilterUrl, buildReplayPlaygroundUrl } from '@/utils/logsUrl';

import InfoRow from './infoRow';
import styles from './logstyles.module.scss';

type Props = {
  request: RequestLog;
  responses: ResponseLog[];
};

const ResponseLogDetailView = ( { request, responses }: Props ) => {
  const [ useLocalTimeZone ] = useLocalZonePreference();

  const formatTimestamp = useCallback( ( timestamp: string | undefined ) => {
    if ( !timestamp ) return undefined;
    const dt = DateTime.fromISO( timestamp, { zone: 'utc' } );
    const zonedDt = useLocalTimeZone ? dt.toLocal() : dt.toUTC();
    return zonedDt.toFormat( 'LLL d, yyyy, h:mm:ss.SSS a (ZZZZ)' );
  }, [ useLocalTimeZone ] );

  const callId = request.meta_data?.call_id as string | undefined;
  const requestPayload = request.meta_data?.request;
  const constellationName = ( request as RequestLog & { constellation_name?: string } ).constellation_name;
  const constellationVersion = ( request as RequestLog & { constellation_version?: string } ).constellation_version;
  const clientId = request.client_id;

  const filterUrl = ( field: Parameters<typeof buildLogsFilterUrl>[0], value: string | undefined ) => ( value ? buildLogsFilterUrl( field, value, 'requestResponseLog' ) : undefined );

  const canReplay = !!request.agent_id && !!request.request_id;
  const handleReplay = () => {
    if ( !request.agent_id || !request.request_id ) return;
    const url = buildReplayPlaygroundUrl( {
      agentId: request.agent_id,
      requestId: request.request_id
    } );
    window.open( url, '_blank', 'noopener,noreferrer' );
  };

  return (
    <div>
      {/* Request Summary */}
      <div className={ styles.card }>
        <div className={ styles.header }>
          <Heading as="h2" size="md" className={ styles.title }>Request Details</Heading>
          {canReplay && (
            <Button
              variant='secondary'
              size='sm'
              icon={ <Icon icon={ FiPlay } size='sm' /> }
              onClick={ handleReplay }
              title='Open the playground in a new tab and resubmit this request'
            >
              Replay
            </Button>
          )}
        </div>
        <InfoRow label="Agent ID" value={ request.agent_id } mono
          filterHref={ filterUrl( 'agentId', request.agent_id ) }
        />
        <InfoRow label="Agent Name" value={ request.agent_name }
          filterHref={ filterUrl( 'agentName', request.agent_name ) }
        />
        <InfoRow label="Constellation Name" value={ constellationName }
          filterHref={ filterUrl( 'constellationName', constellationName ) }
        />
        <InfoRow label="Constellation Version" value={ constellationVersion }
          filterHref={ filterUrl( 'constellationVersion', constellationVersion ) }
        />
        <InfoRow label="Call ID" value={ callId } mono />
        <InfoRow label="Start Time" value={ formatTimestamp( request.start_time ) } />
        <InfoRow label="End Time" value={ formatTimestamp( request.end_time ) } />
        <InfoRow label="Correlation ID" value={ request.correlation_id } mono
          filterHref={ filterUrl( 'correlationId', request.correlation_id ) }
        />
        <InfoRow label="Request ID" value={ request.request_id } mono
          filterHref={ filterUrl( 'requestId', request.request_id ) }
        />
        <InfoRow label="Client ID" value={ clientId } mono
          filterHref={ filterUrl( 'clientId', clientId ) }
        />
        {typeof request.model_id === 'string' && request.model_id && (
          <InfoRow label="Model ID" value={ request.model_id }
            filterHref={ filterUrl( 'modelId', request.model_id ) }
          />
        )}
      </div>

      {/* Request Payload */}
      {requestPayload !== undefined && (
        <div className={ styles.card }>
          <Heading as="h2" size="md" className={ styles.sectionTitle }>Request Payload</Heading>
          <div className={ styles.jsonContainer }>
            <pre className={ styles.jsonContent }>
              {typeof requestPayload === 'string'
                ? requestPayload
                : JSON.stringify( requestPayload, null, 2 )}
            </pre>
          </div>
        </div>
      )}

      {/* Responses */}
      <div className={ styles.card }>
        <Heading as="h2" size="md" className={ styles.sectionTitle }>
          Responses ({responses.length})
        </Heading>

        {responses.length === 0 ? (
          <p className={ styles.empty_message }>No responses found for this request.</p>
        ) : (
          responses.map( ( response, index ) => {
            const responsePayload = response.meta_data?.response;
            return (
              <div
                key={ response.id || index }
                className={ styles.response_item }
              >
                <p className={ styles.response_label }>
                  Response {index + 1}
                </p>
                {response.id && (
                  <InfoRow label="Response ID" value={ response.id } mono />
                )}
                {response.start_time && (
                  <InfoRow label="Time" value={ formatTimestamp( response.start_time ) } />
                )}
                {response.correlation_id && (
                  <InfoRow label="Correlation ID" value={ response.correlation_id } mono
                    filterHref={ filterUrl( 'correlationId', response.correlation_id ) }
                  />
                )}
                {response.request_id && (
                  <InfoRow label="Request ID" value={ response.request_id } mono
                    filterHref={ filterUrl( 'requestId', response.request_id ) }
                  />
                )}
                {responsePayload !== undefined && (
                  <div className={ styles.payload_section }>
                    <p className={ styles.payload_label }>Payload</p>
                    <div className={ styles.jsonContainer }>
                      <pre className={ styles.jsonContent }>
                        {typeof responsePayload === 'string'
                          ? responsePayload
                          : JSON.stringify( responsePayload, null, 2 )}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          } )
        )}
      </div>
    </div>
  );
};

export default ResponseLogDetailView;
